import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { ScenarioDomainKey } from 'lib/evaluation-case/scenario-domains';
import {
  ApiEnvelopeError,
  throwTypedEnvelopeError,
} from 'lib/evaluation-case/api-envelope';
import { calculatingInterval } from 'lib/evaluation-case/polling';
import { picksSearchParams, type ShapePicks } from './footprints';
import type {
  PackageContext,
  RunOverrides,
} from 'lib/evaluation-case/package-context';

/** `GET /evaluation_cases/coverage_preview` (EvaluationCase::CoveragePreview). */
export type CoveragePreviewResponse =
  | {
      status: 'calculating';
      pendingDomains: string[];
      pollIntervalMs?: number | null;
      missingDomains: ScenarioDomainKey[];
      archivedDomains: ScenarioDomainKey[];
    }
  | {
      status: 'ready';
      typeCurveScenarioId: number;
      computedAt: string | null;
      totalSlots: number;
      missingDomains: ScenarioDomainKey[];
      archivedDomains: ScenarioDomainKey[];
      /** The slots every pick covers: what a case-level run of these picks values. */
      intersection: { covered: number; uncovered: number };
      /** unique_slot_id: the uwi for a well slot, the LTD slot_id otherwise. */
      coveredSlotIds: string[];
      uncoveredSlotIds: string[];
      /** The LTD slots by IUFS row id, which the LTD tile is filtered on. */
      coveredIufsIds: number[];
      uncoveredIufsIds: number[];
      /** The places the TC team rejected: outside the counts above, drawn apart. */
      rejectedPlaces: RejectedPlace[];
    };

/** A place the TC team rejected (EvaluationCase::CoveragePreview). */
export interface RejectedPlace {
  iufsId: number;
  /** As the slot grid gives it: "Unvalued in typecurve: <note>". */
  reason: string;
}

export const COVERAGE_PREVIEW_QUERY_KEY = 'evaluation-case:coverage-preview';

/**
 * Which of the package's slots the picks (saved or not) cover, polled while the server computes.
 * Off without a package, with nothing shape-carrying picked (the endpoint needs one), or while
 * `enabled` is false. With run overrides (the eval page) it POSTs them, and the slots are counted
 * as a Calculate with them would value them.
 *
 * @param {PackageContext | null} context - The package, and the Type Curve scenario to count on.
 * @param {ShapePicks} picks - The shape-carrying picks.
 * @param {boolean} enabled - Whether to ask at all.
 * @param {RunOverrides | null} runOverrides - The eval's run overrides, or null on the case page.
 * @return {object} The react-query result.
 */
export function useCoveragePreview(
  context: PackageContext | null,
  picks: ShapePicks,
  enabled = true,
  runOverrides: RunOverrides | null = null
) {
  const params = picksSearchParams(picks);
  const pickKey = params.toString();
  if (context) {
    params.set('packageId', String(context.packageId));
    if (context.typeCurveScenarioId !== null)
      params.set('typeCurveScenarioId', String(context.typeCurveScenarioId));
  }
  return useQuery<CoveragePreviewResponse, Error>({
    queryKey: [
      COVERAGE_PREVIEW_QUERY_KEY,
      context?.packageId,
      context?.typeCurveScenarioId,
      pickKey,
      runOverrides,
    ],
    queryFn: async ({ signal }) => {
      try {
        const request = runOverrides
          ? api.post('evaluation_cases/coverage_preview', {
              searchParams: params,
              json: runOverrides,
              signal,
            })
          : api.get('evaluation_cases/coverage_preview', {
              searchParams: params,
              signal,
            });
        return await request.json<CoveragePreviewResponse>();
      } catch (error) {
        return throwTypedEnvelopeError(
          error,
          ApiEnvelopeError,
          "Couldn't load the package's coverage"
        );
      }
    },
    enabled: enabled && context !== null && pickKey !== '',
    retry: false,
    staleTime: 300_000,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      calculatingInterval(query.state.data, query.state.status),
  });
}

/**
 * The package preview the coverage map shows for a complete case: valued slots only for a case a
 * run would take (all eight assigned, none archived), counted on the form's Type Curve, else the
 * package's. `archived` is a pick the preview found archived since the case loaded (the form
 * doesn't know, and a run would refuse it); `ready` is the answer to draw, once there is one and
 * nothing is archived.
 *
 * @param {PackageContext | null} packageContext - The package, from the eval page's tearsheet.
 * @param {number | null} typeCurveId - The form's Type Curve pick.
 * @param {ShapePicks} picks - The shape-carrying picks.
 * @param {boolean} caseComplete - All eight domains assigned, none archived.
 * @param {RunOverrides | null} runOverrides - The eval's run overrides, or null on the case page.
 * @return {object} `preview` (the query), `archived` and `ready`.
 */
export function usePackagePreview(
  packageContext: PackageContext | null,
  typeCurveId: number | null,
  picks: ShapePicks,
  caseComplete: boolean,
  runOverrides: RunOverrides | null = null
) {
  const context = useMemo<PackageContext | null>(
    () =>
      packageContext && {
        packageId: packageContext.packageId,
        typeCurveScenarioId: typeCurveId ?? packageContext.typeCurveScenarioId,
      },
    [packageContext, typeCurveId]
  );
  const preview = useCoveragePreview(
    context,
    picks,
    caseComplete,
    runOverrides
  );
  const archived =
    caseComplete && preview.data && !preview.isPlaceholderData
      ? preview.data.archivedDomains
      : [];
  const ready =
    caseComplete &&
    preview.data?.status === 'ready' &&
    !preview.isPlaceholderData &&
    archived.length === 0
      ? preview.data
      : null;
  return { preview, archived, ready };
}
